import { jsPDF } from 'jspdf';
import logoUrl from '@/assets/LogoForni.png';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import type { ProveedoresFields } from '@/shared/api/controllers/proveedores';
import type { ComprasFields } from '@/shared/api/controllers/compras';
import type { RetencionPagoFields } from '@/shared/api/controllers/retencionesPago';

/**
 * "Certificado de Retención - Impuesto a las Ganancias" PDF.
 *
 * Layout (top → bottom):
 *
 *   [FN Fornitalia logo]
 *   Certificado de retencion - Impuesto a las ganancias
 *   Comprobante Nr:    │ Ingreso Incluido en: │ Fecha
 *   ───────────────────────────────────────────────────────
 *   Agente de Retención
 *     Nombre o Razón Social │ Dirección       │ Localidad (C.P)
 *     <Fornitalia>          │ <address>       │ <city>
 *     Provincia             │ CUIT
 *     <prov>                │ <cuit>
 *
 *     (Receiver / Sujeto pasible — same shape, no header label)
 *     Nombre o Razón Social │ Dirección       │ Localidad (C.P)
 *     <name>                │ <addr>          │ <city>
 *     Provincia             │ CUIT
 *   ───────────────────────────────────────────────────────
 *   Datos de la retención
 *     Orden de Pago │ Comprobante │ Importe ($)
 *     ┌───────────────────────────────────────────────────┐
 *     │ Régimen │ Monto del comprobante │ Retención Practicada │
 *     │ <conc>  │ $ <isar>              │ $ <ret>              │
 *     ├───────────────────────────────────────────────────┤
 *     │ Comprobante que origina la retención │ Fecha │ Importe │
 *     │ <nro>                                │ <fec> │ $ <tot> │
 *     └───────────────────────────────────────────────────┘
 *
 * Replaces the prior red-band header design with the white,
 * label/value layout from the brand mockup.
 */

export type CertificadoInput = {
  retencion: { id: string; fields: RetencionPagoFields };
  compra: { id: string; fields: ComprasFields };
  proveedor: ProveedoresFields | null;
  /**
   * Other retenciones for the same proveedor + same month. Currently
   * only used to surface the originating compra of each retention in
   * the bottom block; if multiple share a month they're listed in the
   * order received.
   */
  mesAnioSeries: Array<{
    id: string;
    fields: RetencionPagoFields & {
      NumeroComprobante_CO?: string | null;
      FechaComprobante_CO?: string | null;
      Total_CO?: string | null;
      Moneda_CO?: string | null;
    };
  }>;
};

/** Los importes de comprobante van en la moneda de la compra; ISAR y retención, siempre en pesos. */
const monedaDe = (m: string | null | undefined) => (m === 'Dolares' ? 'USD' : 'ARS');

/** AFIP-required issuer data for Fornitalia — hardcoded per PowerApps. */
const ISSUER = {
  name: 'Fornitalia',
  address: 'Av. Elcano 3776, C1427CHO',
  city: 'Capital Federal',
  province: 'Ciudad Autónoma de Buenos Aires',
  cuit: '30-71845033-7',
} as const;

const TEXT_MUTED: [number, number, number] = [110, 110, 110];
const BORDER_COLOR: [number, number, number] = [220, 220, 220];
const ROW_BG: [number, number, number] = [243, 243, 243];

export async function generateCertificadoRetencionPDF(
  input: CertificadoInput,
): Promise<Blob> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const innerWidth = pageWidth - margin * 2;

  // --- top: FN Fornitalia logo ---
  const logoDataUrl = await loadImageAsDataUrl(logoUrl);
  if (logoDataUrl) {
    const logoW = 42;
    const props = doc.getImageProperties(logoDataUrl);
    const logoH = (props.height / props.width) * logoW;
    doc.addImage(logoDataUrl, 'PNG', margin, margin, logoW, logoH);
  }

  let y = margin + 22;

  // --- title ---
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(
    'Certificado de retencion - Impuesto a las ganancias',
    margin,
    y,
  );
  y += 12;

  // --- header info row: Comprobante Nr / Ingreso / Fecha ---
  const fechaRet = input.retencion.fields.FechaComprobante_RP ?? '';
  drawInfoRow(
    doc,
    y,
    [
      {
        label: 'Comprobante Nr:',
        value: input.retencion.id,
        width: innerWidth / 3,
      },
      {
        label: 'Ingreso Incluido en:',
        value: monthLabel(fechaRet),
        width: innerWidth / 3,
      },
      { label: 'Fecha', value: fechaRet || '—', width: innerWidth / 3 },
    ],
    margin,
  );
  y += 14;
  drawSeparator(doc, margin, y, pageWidth - margin);
  y += 8;

  // --- Agente de Retención (issuer) ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(0, 0, 0);
  doc.text('Agente de Retención', margin, y);
  y += 8;

  // Row 1 — issuer 3-column block
  drawInfoRow(
    doc,
    y,
    [
      {
        label: 'Nombre o Razón Social',
        value: ISSUER.name,
        width: innerWidth / 3,
      },
      { label: 'Dirección', value: ISSUER.address, width: innerWidth / 3 },
      {
        label: 'Localidad (C.P)',
        value: ISSUER.city,
        width: innerWidth / 3,
      },
    ],
    margin,
  );
  y += 14;
  drawInfoRow(
    doc,
    y,
    [
      {
        label: 'Provincia',
        value: ISSUER.province,
        width: innerWidth / 2,
      },
      { label: 'CUIT', value: ISSUER.cuit, width: innerWidth / 2 },
    ],
    margin,
  );
  y += 14;

  // --- Sujeto Pasible (receiver) — same field shape as issuer, no
  // separate heading because the layout reads as a continuation. ---
  const r = input.proveedor;
  const receiverName = r?.Nombre_P?.trim() || '—';
  const receiverAddr = r?.DireccionCompleta_P?.trim() || '—';
  const receiverCity = r?.Localidad_P?.trim() || '—';
  const receiverProv = r?.Provincia_P?.trim() || '—';
  const receiverCuit =
    r?.Identificacion_P?.trim() || '—';

  y += 4;
  // Use the row's returned height — receiver addresses commonly wrap
  // to 2+ lines (e.g. "SUCRE ANTONIO DE MARISCAL 2698 Piso:8"), and a
  // hardcoded 14mm advance was crashing the wrapped second line into
  // the Provincia / CUIT row below.
  y += drawInfoRow(
    doc,
    y,
    [
      {
        label: 'Nombre o Razón Social',
        value: receiverName,
        width: innerWidth / 3,
      },
      {
        label: 'Dirección',
        value: receiverAddr,
        width: innerWidth / 3,
      },
      {
        label: 'Localidad (C.P)',
        value: receiverCity,
        width: innerWidth / 3,
      },
    ],
    margin,
  );
  drawInfoRow(
    doc,
    y,
    [
      {
        label: 'Provincia',
        value: receiverProv,
        width: innerWidth / 2,
      },
      { label: 'CUIT', value: receiverCuit, width: innerWidth / 2 },
    ],
    margin,
  );
  y += 12;
  drawSeparator(doc, margin, y, pageWidth - margin);
  y += 8;

  // --- Datos de la retención ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(0, 0, 0);
  doc.text('Datos de la retención', margin, y);
  y += 8;

  // Row: Orden de Pago / Comprobante / Importe
  const compraTotal = parseMoney(input.compra.fields.Total_CO) ?? 0;
  drawInfoRow(
    doc,
    y,
    [
      {
        label: 'Orden de Pago',
        value: input.compra.id || '—',
        width: innerWidth / 3,
      },
      {
        label: 'Comprobante',
        value: input.compra.fields.NumeroComprobante_CO?.trim() || '—',
        width: innerWidth / 3,
      },
      {
        label: 'Importe',
        value: formatMoney(compraTotal, monedaDe(input.compra.fields.Moneda_CO)),
        width: innerWidth / 3,
      },
    ],
    margin,
  );
  y += 16;

  // Régimen sub-row (gray background)
  const isarMonto = parseMoney(input.retencion.fields.ImporteISAR_RP) ?? 0;
  const retMonto = parseMoney(input.retencion.fields.ImporteRetenido_RP) ?? 0;
  drawShadedRow(
    doc,
    y,
    margin,
    innerWidth,
    [
      {
        label: 'Régimen',
        value: input.retencion.fields.Concepto_RP?.trim() || '—',
        width: innerWidth / 3,
      },
      {
        label: 'Monto del comprobante',
        value: formatMoney(isarMonto, 'ARS'),
        width: innerWidth / 3,
      },
      {
        label: 'Retención Practicada',
        value: formatMoney(retMonto, 'ARS'),
        width: innerWidth / 3,
      },
    ],
  );
  y += 18;

  // Comprobante que origina la retención sub-row (gray background)
  // Combine compra-level data: number + date + total. Multiple
  // mesAnioSeries entries are stacked when present.
  const series = input.mesAnioSeries.length > 0
    ? input.mesAnioSeries
    : [
        {
          id: input.compra.id,
          fields: {
            ...input.retencion.fields,
            NumeroComprobante_CO: input.compra.fields.NumeroComprobante_CO,
            FechaComprobante_CO: input.compra.fields.FechaComprobante_CO,
            Total_CO: input.compra.fields.Total_CO,
            Moneda_CO: input.compra.fields.Moneda_CO,
          },
        },
      ];

  for (const s of series) {
    if (y > pageHeight - 40) {
      doc.addPage();
      y = margin;
    }
    drawShadedRow(
      doc,
      y,
      margin,
      innerWidth,
      [
        {
          label: 'Comprobante que origina la retención',
          value:
            s.fields.NumeroComprobante_CO?.trim() ||
            s.fields.NroComprobante_RP?.trim() ||
            '—',
          width: innerWidth * 0.5,
        },
        {
          label: 'Fecha comprobante original',
          value:
            s.fields.FechaComprobante_CO?.trim() ||
            s.fields.FechaComprobante_RP?.trim() ||
            '—',
          width: innerWidth * 0.25,
        },
        {
          label: 'Importe comprobante original',
          value: formatMoney(parseMoney(s.fields.Total_CO) ?? 0, monedaDe(s.fields.Moneda_CO)),
          width: innerWidth * 0.25,
        },
      ],
    );
    y += 18;
  }

  // --- footer signature line ---
  if (y > pageHeight - 50) {
    doc.addPage();
    y = margin + 20;
  }
  const sigY = Math.max(y + 24, pageHeight - 36);
  doc.setDrawColor(150, 150, 150);
  doc.setLineWidth(0.4);
  doc.line(margin + 36, sigY, pageWidth - margin - 36, sigY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...TEXT_MUTED);
  doc.text(
    'Firma y sello del Agente de Retención',
    pageWidth / 2,
    sigY + 5,
    { align: 'center' },
  );
  doc.setTextColor(0, 0, 0);

  // --- generated-on stamp ---
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Generado el ${new Date().toLocaleString('es-AR')}`,
    pageWidth / 2,
    pageHeight - 8,
    { align: 'center' },
  );

  return doc.output('blob');
}

/**
 * Suggest a filename for the generated PDF. Caller passes this as the
 * second argument to `uploadDocument` so the server stores it under a
 * human-readable name. The timestamp prefix is added by the server.
 */
export function certificadoFileName(retencionId: string): string {
  return `CertificadoRetencion-${retencionId}.pdf`;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

type InfoCell = { label: string; value: string; width: number };

/**
 * Renders a horizontal row of label/value cells. Labels are muted gray
 * (9pt), values are bold black (11pt) on the line below. Values that
 * exceed the cell width fall through to additional 9pt-normal lines
 * (5mm baseline-to-baseline) so long addresses keep flowing instead of
 * being chopped or overlapping the next row.
 *
 * Returns the natural height the row consumed so callers can advance
 * `y` past it without a hardcoded value — long-address rows would
 * otherwise overlap the row that follows (as happened with the Sujeto
 * Pasible Provincia / CUIT row before this fix).
 */
function drawInfoRow(
  doc: jsPDF,
  y: number,
  cells: InfoCell[],
  startX: number,
): number {
  let x = startX;
  let maxLines = 1;
  for (const cell of cells) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(cell.label, x, y);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    const lines = doc.splitTextToSize(cell.value, cell.width - 4) as string[];
    doc.text(lines[0] ?? '', x, y + 7);
    if (lines.length > 1) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      for (let i = 1; i < lines.length; i++) {
        doc.text(lines[i], x, y + 7 + 5 * i);
      }
      if (lines.length > maxLines) maxLines = lines.length;
    }
    x += cell.width;
  }
  doc.setTextColor(0, 0, 0);
  // Label baseline (0) + first value baseline (+7) + each extra wrap
  // line (+5) + ~7mm bottom buffer so the next row's label has air.
  return 14 + 5 * (maxLines - 1);
}

/**
 * Renders a single row with a light gray rounded background. Used
 * for the "Datos de la retención" sub-rows so each numeric block
 * reads as one card instead of running together with the section
 * above.
 */
function drawShadedRow(
  doc: jsPDF,
  y: number,
  startX: number,
  width: number,
  cells: InfoCell[],
) {
  doc.setFillColor(...ROW_BG);
  doc.roundedRect(startX, y - 4, width, 16, 1.5, 1.5, 'F');
  let x = startX + 4;
  for (const cell of cells) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text(cell.label, x, y);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(0, 0, 0);
    const lines = doc.splitTextToSize(cell.value, cell.width - 6) as string[];
    doc.text(lines[0] ?? '', x, y + 6);
    x += cell.width;
  }
  doc.setTextColor(0, 0, 0);
}

function drawSeparator(doc: jsPDF, x1: number, y: number, x2: number) {
  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.4);
  doc.line(x1, y, x2, y);
}

// Cache keyed by URL so future shared assets (sello, footer, etc.)
// don't fight for a single slot.
const imageCache = new Map<string, string>();
async function loadImageAsDataUrl(url: string): Promise<string | null> {
  const cached = imageCache.get(url);
  if (cached) return cached;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    imageCache.set(url, dataUrl);
    return dataUrl;
  } catch {
    return null;
  }
}

/**
 * Parse `dd/mm/yyyy` and return `MMM-yyyy` (e.g., "may-2026") for the
 * "Ingreso Incluido en" column. Falls back to the raw value.
 */
function monthLabel(ddmmyyyy: string | null | undefined): string {
  if (!ddmmyyyy) return '—';
  const m = ddmmyyyy.match(/^\d{2}\/(\d{2})\/(\d{4})$/);
  if (!m) return ddmmyyyy;
  const months = [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ];
  const idx = Number(m[1]) - 1;
  return `${months[idx] ?? m[1]}-${m[2]}`;
}
